"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, Banknote, Clock, Flame, Gauge, History, PhoneCall, ShieldAlert, Sparkles, Wrench, Zap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const reasonsList = [{
  id: 1,
  title: "System Age (10+ Years)",
  description: "Standard water heaters and traditional pipe systems are designed to last 8 to 12 years. Beyond this, failure risks multiply rapidly.",
  stat: "10-12 Yrs",
  statLabel: "Average Lifespan"
}, {
  id: 2,
  title: "Higher Energy Bills",
  description: "Sediment buildup and worn heating components force your unit to work twice as hard, visibly inflating monthly electric or gas utility bills.",
  stat: "Up to 30%",
  statLabel: "Efficiency Loss"
}, {
  id: 3,
  title: "Rising Repair Costs",
  description: "When routine emergency repair visits cost more than half the price of a brand new unit, full replacement is the smarter financial choice.",
  stat: "50% Rule",
  statLabel: "Cost Threshold"
}, {
  id: 4,
  title: "Improved Performance & Flow",
  description: "Modern high-efficiency tanks and tankless systems provide endless hot water, consistent pressure, and advanced smart safety controls.",
  stat: "99%",
  statLabel: "Reliability Rate"
}];
const signsList = [{
  id: 1,
  title: "Rust or Discolored Water",
  detail: "Rusty, reddish, or cloudy water from hot taps indicates internal corrosion in your tank or aging iron supply pipes.",
  urgency: "High Risk"
}, {
  id: 2,
  title: "Loud Popping & Rumbling",
  detail: "Hardened mineral sediment trapped at the bottom of the tank overheats and cracks the inner lining, causing audible banging.",
  urgency: "Warning"
}, {
  id: 3,
  title: "Moisture or Puddling at Base",
  detail: "Even minor leaks or moisture pooling around the unit often indicate an internal tank fracture requiring urgent swap-out.",
  urgency: "Immediate Action"
}, {
  id: 4,
  title: "Inadequate or Tepid Hot Water",
  detail: "If your hot water runs cold within minutes or never reaches full temperature, critical heating elements have failed.",
  urgency: "Needs Attention"
}, {
  id: 5,
  title: "Frequent Pilot Outages",
  detail: "Gas water heaters that struggle to maintain a pilot flame or show soot buildup have compromised thermocouple or burner assemblies.",
  urgency: "Safety Hazard"
}, {
  id: 6,
  title: "Low Water Pressure",
  detail: "Extensive calcification and mineral scale inside aging pipes restrict water flow throughout your entire household.",
  urgency: "Degraded System"
}];
export default function ReplacementReasons() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="replacementreasonsandsigns" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Clear Guidance & Practical Advice
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-6">
            Repair or Replace? Know When It Is Time.
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Plumbing emergencies can be costly if caught late. Review the primary reasons for upgrading along with the physical warning signs that indicate a replacement is overdue.
          </p>
        </div>

        {/* Primary Reasons Grid */}
        <div className="mb-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <Zap className="size-5" />
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-foreground">
                Top Reasons to Invest in a New System
              </h3>
              <p className="text-sm text-muted-foreground">
                Why homeowners in Jonesboro choose modern, energy-efficient replacements.
              </p>
            </div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.1
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {reasonsList.map((reason, i) => <motion.div key={reason.id} data-index={i} variants={itemVariants} className="h-full">
                <Card className="h-full bg-card border-border shadow-sm flex flex-col justify-between hover:shadow-md transition-[box-shadow,border-color]">
                  <CardHeader className="p-6 pb-2">
                    <div className="flex items-center justify-between mb-4">
                      <div className="size-9 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center font-bold text-sm">
                        0{reason.id}
                      </div>
                      <span className="text-xs font-semibold text-primary uppercase tracking-wider bg-primary/10 px-2 py-0.5 rounded-sm">
                        {reason.stat}
                      </span>
                    </div>
                    <CardTitle className="text-lg font-bold text-card-foreground mb-2">
                      {reason.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {reason.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 pt-4">
                    <Separator className="mb-4" />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Primary Benefit</span>
                      <span className="font-medium text-foreground">{reason.statLabel}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Warning Signs Section */}
        <div className="rounded-2xl bg-muted/40 border border-border p-6 sm:p-10 lg:p-12 mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <div className="flex items-center gap-2 text-destructive mb-2 font-semibold text-sm">
                <AlertTriangle className="size-4" />
                <span>Immediate Checklist</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
                6 Warning Signs You Need an Urgent Replacement
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              If your system exhibits two or more of these symptoms, call our 24/7 team in Jonesboro for a rapid safety inspection before major water damage occurs.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.1
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {signsList.map((sign, i) => <motion.div key={sign.id} data-index={i} variants={itemVariants} className="bg-card rounded-xl p-6 border border-border shadow-xs flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="size-8 rounded-full bg-destructive/10 text-destructive flex items-center justify-center font-bold text-xs">
                      !
                    </span>
                    <Badge variant="outline" className="text-[11px] font-medium border-border text-foreground">
                      {sign.urgency}
                    </Badge>
                  </div>
                  <h4 className="text-base font-semibold text-card-foreground mb-2">
                    {sign.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {sign.detail}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Bottom Callout / Emergency Assistance */}
        <div className="bg-primary text-primary-foreground rounded-2xl p-8 sm:p-10 lg:p-12 relative overflow-hidden shadow-xl">
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl text-center lg:text-left">
              <span className="inline-flex items-center gap-2 bg-primary-foreground/15 text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full mb-4">
                <ShieldAlert className="size-3.5" />
                Same-Day Emergency Service in Jonesboro, GA
              </span>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3 text-primary-foreground">
                Not sure if you need a quick repair or full replacement?
              </h3>
              <p className="text-primary-foreground/85 text-sm sm:text-base leading-relaxed">
                Our licensed Jonesboro plumbers provide honest diagnostic evaluations with upfront estimates. We never push replacements when a simple fix is safe and cost-effective.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto shrink-0">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-semibold">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4 text-primary" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}